import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { newEventId } from '@/features/timeline/lib/eventId';
import type {
  AIFlagCode,
  ExtractEnvelope,
  ExtractPrescriptionMedication,
  ExtractPrescriptionOutput,
  Profile,
} from '@/types/db';
import type { UploadedAttachment } from '@/features/timeline/lib/upload';
import { buildFlagsFromExtraction, codeToFlag } from '@/features/timeline/lib/aiFlags';
import { FieldFlagsGroup } from '@/features/timeline/components/flag-chip';
import { PhotoLightbox } from '@/features/timeline/components/photo-lightbox';
import { signedPrescriptionUrl } from '@/features/timeline/lib/upload';
import { saveExtraction } from '@/features/timeline/lib/save';
import {
  extractErrorMessage,
  type ExtractErrorCode,
} from '@/features/timeline/lib/extract';
import { PdfPreview } from '@/components/pdf-preview';
import { LabReview } from '@/features/timeline/components/review-lab';
import { ScanReview } from '@/features/timeline/components/review-scan';

const DATE_FLAG_CODES = new Set<AIFlagCode>([
  'unreadable_date',
  'date_converted_from_hijri',
  'date_assumed_dmy',
  'arabic_indic_digits_normalized',
]);
const DIAGNOSIS_FLAG_CODES = new Set<AIFlagCode>(['chief_complaint_not_diagnosis']);

const FREQ_CHIPS = ['Once daily', 'Twice daily', '3× daily', '4× daily'] as const;
const TIMING_CHIPS = ['Before meals', 'After meals', 'At bedtime', 'As needed'] as const;
type FreqChip = typeof FREQ_CHIPS[number];
type TimingChip = typeof TIMING_CHIPS[number];

const INTERVAL_TO_FREQ: [RegExp, FreqChip][] = [
  [/\b(every|each|q\.?)\s*6\s*(h|hr|hrs|hours?)?\b/, '4× daily'],
  [/\b(every|each|q\.?)\s*8\s*(h|hr|hrs|hours?)?\b/, '3× daily'],
  [/\b(every|each|q\.?)\s*12\s*(h|hr|hrs|hours?)?\b/, 'Twice daily'],
  [/\b(every|each|q\.?)\s*24\s*(h|hr|hrs|hours?)?\b/, 'Once daily'],
  [/\b6\s*hourly\b/, '4× daily'],
  [/\b8\s*hourly\b/, '3× daily'],
  [/\b12\s*hourly\b/, 'Twice daily'],
  // Latin medical abbreviations common on Middle East prescriptions
  [/\bqid\b/, '4× daily'],
  [/\btid\b/, '3× daily'],
  [/\bbid\b/, 'Twice daily'],
  [/\bod\b/, 'Once daily'],
];

function detectFreqChip(frequency: string | null): FreqChip | null {
  if (!frequency) return null;
  const lower = frequency.toLowerCase();
  for (const [pattern, chip] of INTERVAL_TO_FREQ) {
    if (pattern.test(lower)) return chip;
  }
  return FREQ_CHIPS.find((c) => lower.includes(c.toLowerCase())) ?? null;
}

function detectTimingChip(frequency: string | null): TimingChip | null {
  if (!frequency) return null;
  const lower = frequency.toLowerCase();
  return TIMING_CHIPS.find((c) => lower.includes(c.toLowerCase())) ?? null;
}

export interface ReviewRouteState {
  profile: Profile;
  eventId: string;
  attachment: UploadedAttachment;
  envelope: ExtractEnvelope | null;
  extractError: ExtractErrorCode | null;
}

const DISCLAIMER =
  'This app stores records. It does not provide medical advice. For clinical decisions, consult a licensed clinician.';

interface EditableMed extends ExtractPrescriptionMedication {
  id: string;
  freqChip: FreqChip | null;
  timingChip: TimingChip | null;
}

interface FormState {
  eventDate: string;
  doctorName: string;
  doctorSpecialty: string;
  clinicName: string;
  clinicPhone: string;
  clinicAddress: string;
  insuranceCompany: string;
  insuranceClaim: string;
  diagnosis: string;
  notes: string;
  medications: EditableMed[];
}

function initFormState(extraction: ExtractPrescriptionOutput | null): FormState {
  return {
    eventDate: extraction?.event_date ?? '',
    doctorName: extraction?.doctor_name ?? '',
    doctorSpecialty: extraction?.doctor_specialty ?? '',
    clinicName: extraction?.clinic_name ?? '',
    clinicPhone: extraction?.clinic_phone ?? '',
    clinicAddress: extraction?.clinic_address ?? '',
    insuranceCompany: extraction?.insurance?.company ?? '',
    insuranceClaim: extraction?.insurance?.claim_number ?? '',
    diagnosis: extraction?.diagnosis ?? '',
    notes: extraction?.notes ?? '',
    medications:
      extraction?.medications?.map((m, i) => ({
        ...m,
        id: `m-${i}`,
        freqChip: detectFreqChip(m.frequency),
        timingChip: detectTimingChip(m.frequency),
      })) ?? [emptyMed()],
  };
}

function emptyMed(): EditableMed {
  return {
    id: newEventId(),
    drug_name: null,
    dosage: null,
    frequency: null,
    duration_days: null,
    flags: [],
    freqChip: null,
    timingChip: null,
  };
}

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

type FieldState = 'ai' | 'edited' | 'user';

function fieldState(
  current: string | number | null | undefined,
  original: string | number | null | undefined,
): FieldState {
  const cur = current === null || current === undefined || current === '' ? null : String(current);
  const orig = original === null || original === undefined || original === '' ? null : String(original);
  if (orig === null) return 'user';
  if (cur === orig) return 'ai';
  return 'edited';
}

function FieldLabel({
  htmlFor,
  text,
  state,
}: {
  htmlFor: string;
  text: string;
  state: FieldState;
}) {
  const dotClass =
    state === 'ai'
      ? 'bg-accent'
      : state === 'edited'
        ? 'bg-flag-warn'
        : 'bg-border-strong';
  const title =
    state === 'ai'
      ? 'AI-filled from your photo'
      : state === 'edited'
        ? 'You edited this'
        : 'Not read — type it in';
  return (
    <label htmlFor={htmlFor} className="label flex items-center gap-1.5">
      <span
        aria-hidden="true"
        title={title}
        className={`h-1.5 w-1.5 rounded-full ${dotClass}`}
      />
      {text}
    </label>
  );
}

function clampDuration(n: number | null): number | null {
  if (n === null || !Number.isFinite(n)) return null;
  if (n < 0 || n > 3650) return null;
  return Math.floor(n);
}

function extractionIsEmpty(e: ExtractPrescriptionOutput): boolean {
  return (
    !e.event_date &&
    !e.doctor_name &&
    !e.clinic_name &&
    !e.diagnosis &&
    !e.notes &&
    (e.medications?.length ?? 0) === 0
  );
}

/**
 * True when Claude returned usable data but signaled it struggled — either
 * `confidence: 'low'` or enough flags (top-level + per-med) that the caregiver
 * should double-check every field before saving.
 */
function extractionIsLowConfidence(e: ExtractPrescriptionOutput): boolean {
  if (e.confidence === 'low') return true;
  const topFlags = e.flags?.length ?? 0;
  const medFlags =
    e.medications?.reduce((sum, m) => sum + (m.flags?.length ?? 0), 0) ?? 0;
  return topFlags + medFlags >= 3;
}

export function ReviewPage() {
  const location = useLocation();
  const state = location.state as ReviewRouteState | null;

  if (!state || !state.profile || !state.attachment) {
    return <Navigate to="/home" replace />;
  }

  const envelope = state.envelope;
  if (envelope?.doc_type === 'lab_report') {
    return (
      <LabReview
        profile={state.profile}
        eventId={state.eventId}
        attachment={state.attachment}
        extraction={envelope.data}
      />
    );
  }
  if (envelope?.doc_type === 'scan') {
    return (
      <ScanReview
        profile={state.profile}
        eventId={state.eventId}
        attachment={state.attachment}
        extraction={envelope.data}
      />
    );
  }

  // Default: prescription, manual-entry fallback, or extract error.
  const extraction =
    envelope?.doc_type === 'prescription' ? envelope.data : null;
  return (
    <PrescriptionReviewBody
      profile={state.profile}
      eventId={state.eventId}
      attachment={state.attachment}
      extraction={extraction}
      extractError={state.extractError}
    />
  );
}

interface PrescriptionReviewBodyProps {
  profile: Profile;
  eventId: string;
  attachment: UploadedAttachment;
  extraction: ExtractPrescriptionOutput | null;
  extractError: ExtractErrorCode | null;
}

function PrescriptionReviewBody({
  profile,
  eventId,
  attachment,
  extraction,
  extractError,
}: PrescriptionReviewBodyProps) {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(() => initFormState(extraction));
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoErr, setPhotoErr] = useState(false);
  const [photoReloadKey, setPhotoReloadKey] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!attachment) return;
    let cancelled = false;
    setPhotoErr(false);
    setPhotoUrl(null);
    signedPrescriptionUrl(attachment.storage_path)
      .then((url) => {
        if (!cancelled) setPhotoUrl(url);
      })
      .catch((err) => {
        console.error('[review] signed url failed', err);
        if (!cancelled) setPhotoErr(true);
      });
    return () => {
      cancelled = true;
    };
  }, [attachment, photoReloadKey]);

  const retryPhoto = useCallback(() => setPhotoReloadKey((k) => k + 1), []);

  const flags = useMemo(
    () => (extraction ? buildFlagsFromExtraction(extraction) : []),
    [extraction],
  );

  const topFlagCodes: AIFlagCode[] = extraction?.flags ?? [];
  const dateFlags = useMemo(
    () => topFlagCodes.filter((c) => DATE_FLAG_CODES.has(c)).map((c) => codeToFlag(c)),
    [extraction],
  );
  const diagnosisFlags = useMemo(
    () => topFlagCodes.filter((c) => DIAGNOSIS_FLAG_CODES.has(c)).map((c) => codeToFlag(c)),
    [extraction],
  );
  const generalFlags = useMemo(
    () =>
      topFlagCodes
        .filter((c) => !DATE_FLAG_CODES.has(c) && !DIAGNOSIS_FLAG_CODES.has(c))
        .map((c) => codeToFlag(c)),
    [extraction],
  );

  const originals = useMemo(
    () => ({
      eventDate: extraction?.event_date ?? null,
      doctorName: extraction?.doctor_name ?? null,
      doctorSpecialty: extraction?.doctor_specialty ?? null,
      clinicName: extraction?.clinic_name ?? null,
      clinicPhone: extraction?.clinic_phone ?? null,
      clinicAddress: extraction?.clinic_address ?? null,
      insuranceCompany: extraction?.insurance?.company ?? null,
      insuranceClaim: extraction?.insurance?.claim_number ?? null,
      diagnosis: extraction?.diagnosis ?? null,
      notes: extraction?.notes ?? null,
      medications: extraction?.medications ?? [],
    }),
    [extraction],
  );
  const emptyExtraction = extraction !== null && extractionIsEmpty(extraction);
  const lowConfidence =
    extraction !== null &&
    !emptyExtraction &&
    extractionIsLowConfidence(extraction);

  const handleCancel = useCallback(() => {
    const dest = profile ? `/profile/${profile.id}` : '/';
    if (typeof window !== 'undefined') {
      const ok = window.confirm(
        'Discard this prescription? The photo and any edits will be lost.',
      );
      if (!ok) return;
    }
    navigate(dest, { replace: true });
  }, [navigate, profile]);

  function updateMed(id: string, patch: Partial<EditableMed>) {
    setForm((f) => ({
      ...f,
      medications: f.medications.map((m) =>
        m.id === id ? { ...m, ...patch } : m,
      ),
    }));
  }

  function addMed() {
    setForm((f) => ({ ...f, medications: [...f.medications, emptyMed()] }));
  }

  function removeMed(id: string) {
    setForm((f) => ({
      ...f,
      medications: f.medications.filter((m) => m.id !== id),
    }));
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!profile || !attachment) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaveError(null);
    setSubmitting(true);
    try {
      const medsClean = form.medications
        .map((m) => ({
          drug_name: nullIfEmpty(m.drug_name),
          dosage: nullIfEmpty(m.dosage),
          frequency: nullIfEmpty(m.frequency),
          duration_days: clampDuration(m.duration_days),
          flags: [],
        }))
        .filter(
          (m) =>
            m.drug_name ||
            m.dosage ||
            m.frequency ||
            m.duration_days !== null,
        );

      await saveExtraction({
        profileId: profile.id,
        eventId,
        eventType: 'prescription',
        eventDate: nullIfEmpty(form.eventDate),
        doctorName: nullIfEmpty(form.doctorName),
        doctorSpecialty: nullIfEmpty(form.doctorSpecialty),
        clinicName: nullIfEmpty(form.clinicName),
        clinicPhone: nullIfEmpty(form.clinicPhone),
        clinicAddress: nullIfEmpty(form.clinicAddress),
        insuranceCompany: nullIfEmpty(form.insuranceCompany),
        insuranceClaim: nullIfEmpty(form.insuranceClaim),
        diagnosis: nullIfEmpty(form.diagnosis),
        notes: nullIfEmpty(form.notes),
        title: null,
        aiFlags: flags,
        medications: medsClean,
        attachments: [attachment],
      });
      navigate(`/profile/${profile.id}`, {
        replace: true,
        state: { justSavedEventId: eventId },
      });
    } catch (err) {
      console.error('[review] save failed', err);
      setSaveError("Couldn't save this record. Please try again.");
      setSubmitting(false);
      submittingRef.current = false;
    }
  }

  return (
    <main className="min-h-screen safe-top pb-32">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Cancel"
            className="h-10 w-10 -ml-1 rounded-full flex items-center justify-center text-ink-muted hover:text-ink active:bg-canvas shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="text-lg font-bold truncate flex-1">Review</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 md:grid md:grid-cols-2 md:gap-6 md:items-start">
        <section className="mb-4 md:mb-0">
          <div className="card overflow-hidden">
            {photoUrl && !photoErr ? (
              attachment.file_type === 'application/pdf' ? (
                <PdfPreview
                  url={photoUrl}
                  fileName={attachment.file_name}
                  variant="full"
                  helperText="Open the PDF to cross-check each field below."
                />
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    aria-label="Tap to zoom the prescription photo"
                    className="block w-full"
                  >
                    <img
                      src={photoUrl}
                      alt="Prescription"
                      className="w-full h-auto max-h-[60vh] object-contain bg-canvas"
                    />
                  </button>
                  <p className="text-xs text-ink-muted px-3 py-2 border-t border-border bg-canvas">
                    Tap the photo to zoom. Check each field below matches the paper.
                  </p>
                </>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-ink-muted text-sm gap-3">
                {photoErr ? (
                  <>
                    <p>Photo couldn't load.</p>
                    <button
                      type="button"
                      onClick={retryPhoto}
                      className="btn-secondary"
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  <p>Loading photo…</p>
                )}
              </div>
            )}
          </div>
        </section>

        <section>
          {extractError && (
            <div className="card p-4 mb-4 border-flag-warn/40 bg-amber-50">
              <p className="text-sm text-flag-warn font-semibold mb-1">
                Couldn't read this one
              </p>
              <p className="text-sm text-ink">
                {extractErrorMessage(extractError)}
              </p>
            </div>
          )}

          {emptyExtraction && !extractError && (
            <div className="card p-4 mb-4 border-flag-warn/40 bg-amber-50">
              <p className="text-sm text-flag-warn font-semibold mb-1">
                We couldn't read much
              </p>
              <p className="text-sm text-ink">
                Type what you see on the prescription below.
              </p>
            </div>
          )}

          {lowConfidence && !extractError && (
            <div className="card p-4 mb-4 border-flag-warn/40 bg-amber-50">
              <p className="text-sm text-flag-warn font-semibold mb-1">
                We read what we could
              </p>
              <p className="text-sm text-ink">
                Handwriting or lighting made parts hard to read. Please check
                every field against the paper before saving.
              </p>
            </div>
          )}

          {extraction && !emptyExtraction && !extractError && !lowConfidence && (
            <div className="rounded-lg border border-accent/30 bg-accent-soft p-3 mb-4">
              <p className="text-sm text-ink mb-2">
                <span className="font-semibold">We read these from your photo.</span>{' '}
                Check each field against the paper before saving.
              </p>
              <div className="flex items-center gap-4 text-xs text-ink-muted">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                  AI-filled
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-flag-warn" aria-hidden="true" />
                  You edited
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-border-strong" aria-hidden="true" />
                  Blank
                </span>
              </div>
            </div>
          )}

          {generalFlags.length > 0 && (
            <div className="mb-4">
              <FieldFlagsGroup flags={generalFlags} />
            </div>
          )}

          <form onSubmit={handleSave} className="card p-5 space-y-4">
            <div>
              <FieldLabel
                htmlFor="eventDate"
                text="Date"
                state={fieldState(form.eventDate, originals.eventDate)}
              />
              <input
                id="eventDate"
                type="date"
                value={form.eventDate}
                max={TODAY_ISO()}
                onChange={(e) =>
                  setForm({ ...form, eventDate: e.target.value })
                }
                className="input"
              />
              <FieldFlagsGroup flags={dateFlags} />
            </div>

            <div>
              <FieldLabel
                htmlFor="doctorName"
                text="Doctor"
                state={fieldState(form.doctorName, originals.doctorName)}
              />
              <input
                id="doctorName"
                type="text"
                value={form.doctorName}
                onChange={(e) =>
                  setForm({ ...form, doctorName: e.target.value })
                }
                className="input"
              />
            </div>

            <div>
              <FieldLabel
                htmlFor="doctorSpecialty"
                text="Specialty"
                state={fieldState(form.doctorSpecialty, originals.doctorSpecialty)}
              />
              <input
                id="doctorSpecialty"
                type="text"
                value={form.doctorSpecialty}
                placeholder="e.g. Pediatrics, Cardiology"
                onChange={(e) =>
                  setForm({ ...form, doctorSpecialty: e.target.value })
                }
                className="input"
              />
            </div>

            <div>
              <FieldLabel
                htmlFor="clinicName"
                text="Clinic"
                state={fieldState(form.clinicName, originals.clinicName)}
              />
              <input
                id="clinicName"
                type="text"
                value={form.clinicName}
                onChange={(e) =>
                  setForm({ ...form, clinicName: e.target.value })
                }
                className="input"
              />
            </div>

            <div>
              <FieldLabel
                htmlFor="clinicPhone"
                text="Phone"
                state={fieldState(form.clinicPhone, originals.clinicPhone)}
              />
              <input
                id="clinicPhone"
                type="tel"
                value={form.clinicPhone}
                placeholder="e.g. +20 2 1234 5678"
                onChange={(e) =>
                  setForm({ ...form, clinicPhone: e.target.value })
                }
                className="input"
              />
            </div>

            <div>
              <FieldLabel
                htmlFor="clinicAddress"
                text="Address"
                state={fieldState(form.clinicAddress, originals.clinicAddress)}
              />
              <input
                id="clinicAddress"
                type="text"
                value={form.clinicAddress}
                placeholder="e.g. 15 Tahrir Square, Cairo"
                onChange={(e) =>
                  setForm({ ...form, clinicAddress: e.target.value })
                }
                className="input"
              />
            </div>

            <div>
              <FieldLabel
                htmlFor="insuranceCompany"
                text="Insurance company"
                state={fieldState(form.insuranceCompany, originals.insuranceCompany)}
              />
              <input
                id="insuranceCompany"
                type="text"
                value={form.insuranceCompany}
                placeholder="e.g. GlobeMed, MetLife"
                onChange={(e) =>
                  setForm({ ...form, insuranceCompany: e.target.value })
                }
                className="input"
              />
            </div>

            <div>
              <FieldLabel
                htmlFor="insuranceClaim"
                text="Claim / policy #"
                state={fieldState(form.insuranceClaim, originals.insuranceClaim)}
              />
              <input
                id="insuranceClaim"
                type="text"
                value={form.insuranceClaim}
                placeholder="e.g. CLM-20240412-001"
                onChange={(e) =>
                  setForm({ ...form, insuranceClaim: e.target.value })
                }
                className="input"
              />
            </div>

            <div>
              <FieldLabel
                htmlFor="diagnosis"
                text="Diagnosis"
                state={fieldState(form.diagnosis, originals.diagnosis)}
              />
              <input
                id="diagnosis"
                type="text"
                value={form.diagnosis}
                onChange={(e) =>
                  setForm({ ...form, diagnosis: e.target.value })
                }
                className="input"
              />
              <FieldFlagsGroup flags={diagnosisFlags} />
            </div>

            <div>
              <FieldLabel
                htmlFor="notes"
                text="Notes"
                state={fieldState(form.notes, originals.notes)}
              />
              <textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="input py-2 min-h-[72px]"
                rows={3}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="label mb-0">Medications</span>
                <button
                  type="button"
                  onClick={addMed}
                  className="text-sm text-accent hover:text-accent-hover font-medium"
                >
                  + Add
                </button>
              </div>
              <ul className="space-y-3">
                {form.medications.map((med, idx) => (
                  <li
                    key={med.id}
                    className="rounded-lg border border-border border-l-4 border-l-evt-rx-rail p-3 bg-canvas"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-evt-rx-ink">
                        Medication {idx + 1}
                      </p>
                      {form.medications.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMed(med.id)}
                          aria-label={`Remove medication ${idx + 1}`}
                          className="h-11 w-11 -m-2 rounded-full text-ink-muted hover:text-flag-block active:bg-canvas flex items-center justify-center"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5"
                            aria-hidden="true"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {(med.flags ?? []).length > 0 && (
                      <FieldFlagsGroup
                        flags={(med.flags ?? []).map((c) =>
                          codeToFlag(c as AIFlagCode, med.drug_name || undefined),
                        )}
                      />
                    )}
                    <input
                      type="text"
                      placeholder="Drug name"
                      value={med.drug_name ?? ''}
                      onChange={(e) =>
                        updateMed(med.id, { drug_name: e.target.value || null })
                      }
                      className={`input mb-3${(med.flags ?? []).length > 0 ? ' mt-2' : ''}`}
                    />

                    {/* Dose */}
                    <div className="mb-3">
                      <p className="text-xs font-medium text-ink-muted mb-1">Dose</p>
                      <input
                        type="text"
                        placeholder="e.g. 1 capsule, 10 mg"
                        value={med.dosage ?? ''}
                        onChange={(e) =>
                          updateMed(med.id, { dosage: e.target.value || null })
                        }
                        className="input"
                      />
                    </div>

                    {/* Schedule with quick-pick chips */}
                    <div className="mb-3">
                      <p className="text-xs font-medium text-ink-muted mb-1">Schedule</p>
                      <input
                        type="text"
                        placeholder="e.g. twice daily after meals"
                        value={med.frequency ?? ''}
                        onChange={(e) =>
                          updateMed(med.id, {
                            frequency: e.target.value || null,
                            freqChip: detectFreqChip(e.target.value || null),
                            timingChip: detectTimingChip(e.target.value || null),
                          })
                        }
                        className="input mb-2"
                      />
                      <div className="grid grid-cols-4 gap-1.5">
                        {FREQ_CHIPS.map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            onClick={() => {
                              const next = med.freqChip === chip ? null : chip;
                              const composed = [next, med.timingChip].filter(Boolean).join(' ') || null;
                              updateMed(med.id, { freqChip: next, frequency: composed });
                            }}
                            className={`h-11 rounded-lg text-xs font-medium border transition-colors leading-none ${
                              med.freqChip === chip
                                ? 'bg-accent text-white border-accent'
                                : 'bg-surface border-border text-ink-muted hover:border-accent hover:text-accent active:bg-canvas'
                            }`}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                      <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                        {TIMING_CHIPS.map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            onClick={() => {
                              const next = med.timingChip === chip ? null : chip;
                              const composed = [med.freqChip, next].filter(Boolean).join(' ') || null;
                              updateMed(med.id, { timingChip: next, frequency: composed });
                            }}
                            className={`h-11 rounded-lg text-xs font-medium border transition-colors leading-none px-1 ${
                              med.timingChip === chip
                                ? 'bg-accent text-white border-accent'
                                : 'bg-surface border-border text-ink-muted hover:border-accent hover:text-accent active:bg-canvas'
                            }`}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Period of treatment */}
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-medium text-ink-muted whitespace-nowrap">Period of treatment</p>
                      <input
                        type="number"
                        placeholder="—"
                        value={med.duration_days ?? ''}
                        min={0}
                        onChange={(e) =>
                          updateMed(med.id, {
                            duration_days: e.target.value
                              ? Number.parseInt(e.target.value, 10)
                              : null,
                          })
                        }
                        className="input w-20 text-center"
                      />
                      <span className="text-xs text-ink-muted">days</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {saveError && (
              <p className="text-sm text-flag-block" role="alert">
                {saveError}
              </p>
            )}
          </form>

          <div className="rounded-lg border border-border bg-canvas p-3 mt-4">
            <p className="text-sm text-ink-muted">{DISCLAIMER}</p>
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 safe-bottom bg-surface border-t border-border px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="btn-secondary px-4"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            aria-describedby={submitting ? 'save-hint' : undefined}
            className="btn-primary flex-1"
          >
            {submitting ? 'Saving…' : 'Save to timeline'}
          </button>
        </div>
        {submitting && (
          <p
            id="save-hint"
            className="max-w-3xl mx-auto mt-1.5 text-[11px] text-ink-muted text-center"
          >
            Writing record — don't close the app.
          </p>
        )}
      </div>

      {lightboxOpen && photoUrl && (
        <PhotoLightbox
          src={photoUrl}
          alt="Prescription"
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </main>
  );
}

function nullIfEmpty(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
